import type { Level } from "@/model/baseTypes";
import type { StatusDistributionSource } from "@/model/status";
import type { LogoutReason } from "@/router/types";

import { webEvent } from "./event";

/**
 * Called when the user leaves the app (eg. closes the tab)
 */
export const appClosed = () => webEvent("app", "closed");

/**
 * Triggered when the tab's visibility state changes
 * https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilitychange_event
 */
export const tabVisibilityStateChanged = (visibility: "hidden" | "visible") =>
  webEvent("app", "tab_visibility_state_changed", { visibility });

/**
 * Triggered when the user is logged out, either by themselves or by the app
 */
export const userLoggedOut = (reason: LogoutReason) => webEvent("user", "logged_out", { reason });

// Triggered when the user changes the tab in any tabbed modal
export const modalTabChanged = (tab: string) => webEvent("modal", "tab_changed", { tab });

// Triggered when the user marks an objective as committed / uncommmitted
export const objectiveMarkedAs = (id: string, isStretch: boolean, level: Level) =>
  webEvent("team_objectives_modal", "objective_marked_as", { objectiveId: id, isStretch, level });

export const switchSessionOrgTreeLevelSelected = (level: Level) =>
  webEvent("switch_session", "org_tree_level_selected", { level });

/**
 * Triggered when the user changes a setting in the Settings Modal
 */
export const userSettingChanged = ({
  key,
  fromValue,
  toValue,
}: {
  key: string;
  fromValue?: string | boolean | number;
  toValue?: string | boolean | number;
}) => webEvent("user_setting", "changed", { key, fromValue, toValue });

// Triggered when the status distribution popup is shown
export const statusDistributionShown = (statusDistributionType: StatusDistributionSource) =>
  webEvent("status_distribution", "shown", { statusDistributionType });

// Triggered when the status distribution popup is hidden
export const statusDistributionHidden = (statusDistributionType: StatusDistributionSource) =>
  webEvent("status_distribution", "hidden", { statusDistributionType });

// Triggered when the user adds a timer
export const timerAdded = (timerScope: "board" | "art") =>
  webEvent("timer", "added", { timerScope });

// Triggered when the user starts a timer
export const timerStarted = (timerScope: "board" | "art") =>
  webEvent("timer", "started", { timerScope });

export const breakdownRowToggled = (open: boolean) =>
  webEvent("breakdown", "row_toggled", { open });

/** @public Retained until the event is retired in Amplitude. */
export const breakdownPivotAxesChanged = (rows: string, columns: string) =>
  webEvent("breakdown", "pivot_axes_changed", { rows, columns });

export const mapGroupingToggled = (grouped: boolean) =>
  webEvent("map", "grouping_toggled", { grouped });

export const mapAxesChanged = (rows: string, columns: string) =>
  webEvent("map", "axes_changed", { rows, columns });

export type MapInteraction =
  | "filter"
  | "group"
  | "change_axes"
  | "zoom"
  | "pan"
  | "select"
  | "drag"
  | "reset_view";

export const mapClosed = (interactions: MapInteraction[], durationSeconds: number) =>
  webEvent("map", "closed", { interactions, durationSeconds });

export const jqlQueryCopied = () => webEvent("jql", "query_copied");
